import { takeLatest, all, put, call, select } from 'redux-saga/effects';

import axios from '../../axiosInstance';
import requests from '../../requests';

import { authActionTypes } from '../auth/auth.types';
import { workspaceActionTypes } from './workspace.types';

import {
    workspaceInitRequest,
    workspaceInitSuccess,
    workspaceInitFailure,
    workspaceSwitchSuccess,
    workspaceFetchTeamsSuccess,
    workspaceFetchTeamsFailure,
    workspaceFetchMembersSuccess,
    workspaceFetchMembersFailure,
    workspaceReset,
} from './workspace.actions';

import { getPlanRestrictions } from './workspace.plans';

// ---------------------------------------------------------------------------
// LocalStorage helpers — remember which workspace the user last used.
// ---------------------------------------------------------------------------

const ACTIVE_WORKSPACE_KEY = 'activeWorkspaceId';

function getStoredWorkspaceId() {
    try {
        return localStorage.getItem(ACTIVE_WORKSPACE_KEY);
    } catch (_) {
        return null;
    }
}

function storeWorkspaceId(id) {
    try {
        localStorage.setItem(ACTIVE_WORKSPACE_KEY, id);
    } catch (_) {}
}

function pickActiveWorkspace(list, storedId) {
    if (storedId) {
        const found = list.find((w) => w.id === storedId);
        if (found) return found;
    }
    return list[0] || null;
}

// ---------------------------------------------------------------------------
// Sagas
// ---------------------------------------------------------------------------

// Called after every successful sign-in or session check.
// Fetches workspaces, resolves the active one, and derives plan restrictions.
export function* initWorkspace() {
    yield put(workspaceInitRequest());
    try {
        const { data } = yield axios.get(requests.workspaces);
        const list = data?.data || [];

        const storedId = getStoredWorkspaceId();
        const currentWorkspace = pickActiveWorkspace(list, storedId);

        if (currentWorkspace) {
            storeWorkspaceId(currentWorkspace.id);
        }

        const planName = currentWorkspace?.pricing_plan || 'free';
        const restrictions = getPlanRestrictions(planName);

        yield put(workspaceInitSuccess({
            list,
            currentWorkspace,
            plan: planName,
            restrictions,
        }));
    } catch (e) {
        yield put(workspaceInitFailure(e));
    }
}

// Switch to a different workspace (picked from the already-fetched list).
export function* switchWorkspace({ payload: workspaceId }) {
    try {
        const list = yield select((state) => state.workspace.list);
        const workspace = list.find((w) => w.id === workspaceId);
        if (!workspace) return;

        storeWorkspaceId(workspaceId);

        const planName = workspace.pricing_plan || 'free';
        const restrictions = getPlanRestrictions(planName);

        yield put(workspaceSwitchSuccess({
            currentWorkspace: workspace,
            plan: planName,
            restrictions,
        }));
    } catch (_) {
        // Non-critical — silent fail
    }
}

// Fetch teams for the given workspace.
export function* fetchWorkspaceTeams({ payload: workspaceId }) {
    try {
        const url = `${requests.workspaces}/${workspaceId}/teams`;
        const { data } = yield axios.get(url);
        yield put(workspaceFetchTeamsSuccess(data?.data || []));
    } catch (e) {
        yield put(workspaceFetchTeamsFailure(e));
    }
}

// Fetch members for the given workspace.
export function* fetchWorkspaceMembers({ payload: workspaceId }) {
    try {
        const url = `${requests.workspaces}/${workspaceId}/members`;
        const { data } = yield axios.get(url);
        yield put(workspaceFetchMembersSuccess(data?.data || []));
    } catch (e) {
        yield put(workspaceFetchMembersFailure(e));
    }
}

// Clear workspace state on sign-out.
export function* resetWorkspaceState() {
    yield put(workspaceReset());
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

export function* onSignInSuccess() {
    // Triggers on both email login and session restore (checkUserSession).
    yield takeLatest(authActionTypes.SIGN_IN_SUCCESS, initWorkspace);
}

export function* onWorkspaceSwitchRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_SWITCH_REQUEST, switchWorkspace);
}

export function* onWorkspaceFetchTeamsRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_FETCH_TEAMS_REQUEST, fetchWorkspaceTeams);
}

export function* onWorkspaceFetchMembersRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_FETCH_MEMBERS_REQUEST, fetchWorkspaceMembers);
}

export function* onSignOutSuccess() {
    yield takeLatest(authActionTypes.SIGN_OUT_SUCCESS, resetWorkspaceState);
}

export function* workspaceSagas() {
    yield all([
        call(onSignInSuccess),
        call(onWorkspaceSwitchRequest),
        call(onWorkspaceFetchTeamsRequest),
        call(onWorkspaceFetchMembersRequest),
        call(onSignOutSuccess),
    ]);
}
